import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, IconButton, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { CreditoDaClinica, TipoDeCredito } from '@/data/tipos';
import { CampoDeMotivo, TIPOS_DE_CREDITO, centavosDoTexto, motivoValido } from './apoioDoPainel';

function mensagem(e: unknown): string {
    return e instanceof Error ? e.message : 'Tente de novo.';
}

/**
 * Os créditos da clínica.
 *
 * Crédito abate a PRÓXIMA fatura de assinatura que for gerada, do mais antigo
 * ao mais novo. O que já abateu não se reescreve: o valor dele está numa fatura
 * emitida.
 */
export function CreditosDaClinica({ clinicaId, clinica, creditos }: {
    clinicaId: string;
    clinica: string;
    creditos: CreditoDaClinica[];
}) {
    const [editando, setEditando] = useState<CreditoDaClinica | 'novo' | null>(null);
    const [excluindo, setExcluindo] = useState<CreditoDaClinica | null>(null);

    const vivos = creditos.filter((c) => !c.excluidoEm);
    const saldo = vivos.reduce((s, c) => s + c.saldoCentavos, 0);
    const concedido = creditos.reduce((s, c) => s + c.valorCentavos, 0);
    const consumido = vivos.reduce((s, c) => s + (c.valorCentavos - c.saldoCentavos), 0);

    const colunas: DataTableColumn<CreditoDaClinica>[] = [
        { key: 'criadoEm', header: 'Concedido em', width: 130, render: (c) => dataSimples(c.criadoEm.slice(0, 10)) },
        {
            key: 'tipo', header: 'Tipo', width: 150,
            render: (c) => <Badge tone="neutral">{TIPOS_DE_CREDITO.find((t) => t.value === c.tipo)?.label ?? c.tipo}</Badge>,
        },
        { key: 'motivo', header: 'Motivo' },
        { key: 'autorNome', header: 'Por', width: 140, render: (c) => c.autorNome ?? '—' },
        { key: 'valorCentavos', header: 'Valor', align: 'right', width: 120, render: (c) => moeda(c.valorCentavos) },
        {
            key: 'saldoCentavos', header: 'Saldo', align: 'right', width: 130,
            render: (c) => (c.excluidoEm
                ? <Badge tone="neutral">excluído</Badge>
                : <span style={{ fontWeight: 600, color: c.saldoCentavos > 0 ? 'var(--success-strong)' : 'var(--text-tertiary)' }}>{moeda(c.saldoCentavos)}</span>),
        },
        {
            key: 'acoes', header: '', align: 'right', width: 90,
            render: (c) => (c.excluidoEm ? null : (
                <span style={{ display: 'inline-flex', gap: 4 }}>
                    {pode('creditos.alterar') && c.saldoCentavos === c.valorCentavos && (
                        <IconButton size="sm" icon={<Icon name="pencil" size={14} />} label={`Editar crédito de ${moeda(c.valorCentavos)}`} onClick={() => setEditando(c)} />
                    )}
                    {pode('creditos.excluir') && (
                        <IconButton size="sm" icon={<Icon name="trash-2" size={14} />} label={`Excluir crédito de ${moeda(c.valorCentavos)}`} onClick={() => setExcluindo(c)} />
                    )}
                </span>
            )),
        },
    ];

    return (
        <>
            <Card
                title="Créditos"
                subtitle="Abatidos na próxima fatura de assinatura gerada, do mais antigo ao mais novo"
                padding={0}
                actions={pode('creditos.adicionar') ? (
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setEditando('novo')}>Adicionar crédito</Button>
                ) : undefined}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, padding: 16 }}>
                    <KpiCard label="Saldo disponível" value={moeda(saldo)} hint="abatido na próxima fatura" icon={<Icon name="gift" size={16} />} />
                    <KpiCard label="Concedido" value={moeda(concedido)} hint={`${creditos.length} ${creditos.length === 1 ? 'lançamento' : 'lançamentos'}`} icon={<Icon name="receipt" size={16} />} />
                    <KpiCard label="Já abatido" value={moeda(consumido)} hint="em faturas geradas" icon={<Icon name="check" size={16} />} />
                </div>
                <DataTable
                    columns={colunas} rows={creditos} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum crédito concedido a esta clínica."
                />
            </Card>

            <CreditoModal clinicaId={clinicaId} clinica={clinica} credito={editando} onFechar={() => setEditando(null)} />
            <ExcluirCreditoModal credito={excluindo} onFechar={() => setExcluindo(null)} />
        </>
    );
}

function CreditoModal({ clinicaId, clinica, credito, onFechar }: {
    clinicaId: string;
    clinica: string;
    credito: CreditoDaClinica | 'novo' | null;
    onFechar: () => void;
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [tipo, setTipo] = useState<TipoDeCredito>('cortesia');
    const [valor, setValor] = useState('');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!credito) return;
        setErro(null);
        if (credito === 'novo') {
            setTipo('cortesia');
            setValor('');
            setMotivo('');
            return;
        }
        setTipo(credito.tipo);
        setValor((credito.valorCentavos / 100).toFixed(2).replace('.', ','));
        setMotivo(credito.motivo);
    }, [credito]);

    const centavos = centavosDoTexto(valor);
    const novo = credito === 'novo';

    const salvar = async () => {
        if (!credito || centavos === null) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().salvarCredito(clinicaId, novo ? null : credito.id, { tipo, valorCentavos: centavos, motivo });
            aviso({ title: novo ? 'Crédito adicionado' : 'Crédito alterado', description: `${moeda(centavos)} · ${clinica}` });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={credito !== null} onClose={onFechar} width={480}
            title={novo ? 'Adicionar crédito' : 'Editar crédito'}
            description={`Abatido na próxima fatura de assinatura de ${clinica}.`}
            dirty={novo ? valor.trim().length > 0 || motivo.trim().length > 0 : true}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button loading={salvando} disabled={centavos === null || centavos <= 0 || !motivoValido(motivo)} onClick={() => { void salvar(); }}>
                        {novo ? 'Adicionar' : 'Salvar'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
                {erro && <div style={{ gridColumn: '1 / -1' }}><Alert tone="danger" dismissible={false}>{erro}</Alert></div>}
                <FormField label="Tipo" htmlFor="credito-tipo" required>
                    <Select id="credito-tipo" value={tipo} onChange={(v) => setTipo(v as TipoDeCredito)} options={TIPOS_DE_CREDITO} />
                </FormField>
                <FormField label="Valor" htmlFor="credito-valor" required hint="Em reais">
                    <Input id="credito-valor" value={valor} onChange={setValor} placeholder="0,00" />
                </FormField>
                <div style={{ gridColumn: '1 / -1' }}>
                    <CampoDeMotivo id="credito-motivo" valor={motivo} onChange={setMotivo} />
                </div>
            </div>
        </Modal>
    );
}

function ExcluirCreditoModal({ credito, onFechar }: { credito: CreditoDaClinica | null; onFechar: () => void }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (credito) {
            setMotivo('');
            setErro(null);
        }
    }, [credito]);

    const excluir = async () => {
        if (!credito) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().excluirCredito(credito.id, motivo);
            aviso({ title: 'Crédito excluído', description: `${moeda(credito.valorCentavos)} · registrado na auditoria`, tone: 'warning' });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={credito !== null} onClose={onFechar} width={480} tone="danger" title="Excluir crédito?"
            description={credito ? `${moeda(credito.valorCentavos)} concedido em ${dataSimples(credito.criadoEm.slice(0, 10))}.` : ''}
            dirty={motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button variant="danger" loading={salvando} disabled={!motivoValido(motivo)} onClick={() => { void excluir(); }}>Excluir crédito</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {credito && (
                    <Alert tone="warning" dismissible={false} title={credito.saldoCentavos > 0 ? 'O saldo sai da próxima fatura' : 'Este crédito já foi todo abatido'}>
                        {credito.saldoCentavos > 0
                            ? `Há ${moeda(credito.saldoCentavos)} de saldo não usado: a próxima fatura sobe nesse valor.`
                            : 'A exclusão só muda o histórico. O registro continua, com quem excluiu e por quê.'}
                    </Alert>
                )}
                <CampoDeMotivo id="credito-exclusao-motivo" valor={motivo} onChange={setMotivo} />
            </div>
        </Modal>
    );
}
